import { Box, styled, Pill, Text } from '@/theme/index';

/**
 * Props for the OutlinedPillCard component.
 */
export interface OutlinedPillCardProps {
  /**
   * The color of the outline pill.
   */
  outlineColor: 'primary' | 'secondary' | 'success' | 'error' | 'warning';
  /**
   * The content of the OutlinedPillCard.
   */
  children: React.ReactNode;
  /**
   * Whether the pill should be outlined or not.
   */
  outlined?: boolean;
  /**
   * The text inside the chip.
   */
  chipText: string;
  /**
   * The color of the chip text.
   */
  chipTextColor: string;
}

/**
 * A component that renders an outlined pill card with optional content inside.
 * @param {OutlinedPillCardProps} props - The props for the component.
 * @returns {JSX.Element} - The JSX element representing the OutlinedPillCard.
 */
export function OutlinedPillCard({
  children,
  outlined,
  chipText,
  outlineColor,
  chipTextColor,
}: OutlinedPillCardProps) {
  const OutLineChip = styled(Pill)(({ theme }) => ({
    position: 'absolute',
    top: '-16px',
    backgroundColor: `${theme.palette[outlineColor]?.main}`,
  }));

  const StyledBox = styled(Box)(({ theme }) => ({
    border: outlined
      ? `2px solid ${theme.palette[outlineColor]?.main}`
      : 'none',
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(4),
    position: 'relative',
  }));

  return (
    <StyledBox data-cy="pill-container">
      <OutLineChip label={<Text color={chipTextColor}>{chipText}</Text>} />
      {children}
    </StyledBox>
  );
}
